import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, doc, limit as fsLimit, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { paymentLabel, financialLabel } from "../lib/payment";
import { ChannelBadge, channelOf } from "../components/ChannelBadge";
import { FastShippingBadge } from "../components/FastShippingBadge";
import { useCan, useSession } from "../lib/auth";
import { useDoc } from "../hooks/useDoc";
import { useCollection } from "../hooks/useCollection";
import { useCallableAction } from "../hooks/useCallableAction";
import { useToast } from "../components/Toast";
import { StatusBadge } from "../components/StatusBadge";
import { OrderStatusBadge } from "../components/OrderStatusBadge";
import { useProductionStageLabels, stageLabelOf } from "../hooks/useProductionStageLabels";
import { LoadingBlock, Spinner } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { Collapsible } from "../components/Collapsible";
import { tl, n, dt, date } from "../lib/format";
import {
  CARGO_PROVIDERS, DEFAULT_CARGO_PROVIDER, buildTrackingUrl,
  resolveProvider, isPlaceholderProvider, looksLikeHorozNumber,
} from "../lib/cargo";
import type {
  SetOrderErpStatusInput,
  SetOrderErpStatusResult,
} from "../hooks/callableResults";

/**
 * Sipariş detayı — eski `order_single.php`nin karşılığı.
 *
 * DÜZELTİLEN DAVRANIŞLAR (analiz §2.2 ve §2.8.1)
 * · Adres artık TEK textarea'ya düşmüyor. Eski `set_address(string)` çağrısı
 *   adres alanlarını bozuyordu; adres alan alan gösterilir ve Shopify
 *   otoritesinde olduğu için buradan DEĞİŞTİRİLMEZ.
 * · Kalem adedi/fiyatı buradan düzenlenmez: eski ekran adet değişince ürünün
 *   GÜNCEL fiyatını kullanıp indirimi siliyordu. Tutar alanları Shopify
 *   otoritesindedir (Rules da istemci yazmasını reddeder).
 * · Statü değişimi ve kargo bilgisi callable üzerinden gider (Shopify tag /
 *   fulfillment yan etkileri sunucuda). Hata mesajı aynen gösterilir.
 * · Üretim notu da callable üzerinden gider (`updateOrderDetails`). Doğrudan
 *   `updateDoc` ile yazılıyordu; Rules yalnızca "hangi alana dokunulabilir"e
 *   bakar, alan doğrulaması ve denetim kaydı SUNUCUDA yapılır. Sunucuda aynı
 *   işi yapan uç zaten vardı ve ekran onu atlıyordu.
 * · Maliyet/kâr bloğu yalnızca financeView (boss) için ve AYRI dokümandan
 *   (`orders/{id}/private/finance`) okunur.
 */

const CALLABLE_SET_STATUS = "setOrderErpStatus";
/** Üretim notu/adres/not güncelleme — doğrudan Firestore yazmanın yerine geçer. */
const CALLABLE_UPDATE_DETAILS = "updateOrderDetails";
const CALLABLE_SAVE_TRACKING = "saveShipmentTracking";
/** Shopify'a yazılamamış kargo kaydını yeniden gönderir (izin düzeldikten sonra). */
const CALLABLE_RETRY_FULFILLMENT = "retryShopifyFulfillment";
/** UPS'ten canlı kargo durumu çeker ve siparişe yazar. */
const CALLABLE_TRACK_SHIPMENT = "trackShipment";
/** Sipariş kalemleri. Shopify terminolojisi; indeksler de bu adla tanımlı. */
const ITEMS_SUBCOLLECTION = "lineItems";

// Kargo firmaları TEK kaynaktan gelir (lib/cargo.ts). Eski sistemde liste
// order_single.php içinde sabitti ve iki ekran birbirinden ayrışabiliyordu.

interface Address {
  formatted?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
  phone?: string;
  /** Kurumsal müşteri ünvanı — e-Faturada VKN ile birlikte gerekir. */
  company?: string;
}

/** Sipariş/kalem seviyesindeki müşteri özel isteği. */
interface Attribute {
  name?: string;
  value?: string;
}

interface ShippingLine {
  title?: string;
  code?: string;
  carrierIdentifier?: string;
  custom?: boolean;
  price?: number;
  discountedPrice?: number;
}

interface Transaction {
  transactionId?: string;
  kind?: string;
  status?: string;
  gateway?: string;
  /** Shopify'ın kaydettiği HAM tutar — mutabakatın dayanağı. */
  amount?: number;
  /**
   * Havale indirimi düşülmüş, KASAYA GİREN tutar.
   * İndirim Shopify'a hiç girmediği için `amount` sözleşme tutarını taşır;
   * müşterinin bankadan gönderdiği para bu alandadır (bkz.
   * `shopify/handlers/orders.ts` §TAHSİLAT DA İNDİRİME TABİDİR).
   */
  effectiveAmount?: number;
  processedAt?: { toDate: () => Date } | null;
  manual?: boolean;
}

interface Payments {
  received?: number;
  /** İndirimsiz tahsilat — Shopify'ın rakamı, mutabakat için. */
  receivedGross?: number;
  outstanding?: number;
  fullyPaid?: boolean;
  gateways?: string[];
  transactions?: Transaction[];
}

interface TrackingInfo {
  provider?: string;
  customProvider?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  shippedAt?: { toDate: () => Date } | null;
  /** Eski/alternatif alan adları (veri modeli `tracking` map'i). */
  company?: string;
  number?: string;
  url?: string;
  /**
   * Shopify senkron damgası (`functions/src/orders/shipment.ts`).
   * `state`: "ok" | "failed" | "skipped". Sunucu bunu YAZIYOR ama hiçbir ekran
   * OKUMUYORDU: takip numarası Shopify'a gitmemiş sipariş, ekranda normal
   * kargolanmış siparişten ayırt edilemiyordu.
   */
  shopifySync?: { ok?: boolean; state?: string; error?: string };
  /**
   * UPS'ten çekilen canlı durum (`functions/src/cargo/track.ts`).
   * Yalnızca `tracking` haritasında tutulur; `trackingInfo` ERP formunun yazdığı
   * alanları taşır, bu ise TAŞIYICIDAN gelen veridir — ikisi karışmasın.
   */
  carrierStatus?: string;
  carrierStatusLabel?: string;
  carrierDescription?: string;
  deliveredAt?: string | null;
  checkedAt?: { toDate: () => Date } | null;
  events?: Array<{ at?: string | null; status?: string; description?: string; location?: string }>;
}

interface OrderDoc {
  /** Satış kanalı — "shopify" | "trendyol" | "legacy". Eski kayıtlarda yok. */
  channel?: string | null;
  trendyol?: {
    orderNumber?: string;
    status?: string;
    packageId?: string;
    agreedDeliveryDate?: number | null;
  } | null;

  orderName?: string;
  erpStatus?: string;
  erpStatusLabel?: string;
  erpStatusChangedAt?: { toDate: () => Date } | null;
  financialStatus?: string;
  fulfillmentStatus?: string;
  createdAt?: { toDate: () => Date } | null;
  cancelledAt?: { toDate: () => Date } | null;
  currency?: string;
  /**
   * `total/subtotal/tax/discount` = ORİJİNAL (sipariş anındaki sözleşme) tutar,
   * `current*` = iade/düzenleme sonrası GÜNCEL tutar. İkisi farklıysa ekran
   * ikisini de gösterir; eskiden bu iki anlam aynı alandaydı ve tutar sessizce
   * değişiyordu.
   */
  totals?: {
    total?: number;
    subtotal?: number;
    discount?: number;
    tax?: number;
    shipping?: number;
    refunded?: number;
    currentTotal?: number;
    currentSubtotal?: number;
    currentDiscount?: number;
    currentTax?: number;
      /** Havale indirimi (kuruş) — Shopify bilmez, sunucuda hesaplanır. */
    bankTransferDiscount?: number;
    /** Toplam − havale indirimi (kuruş). */
    effectiveTotal?: number;
    bankTransferDiscountPercent?: number;
  };
  customer?: { firstName?: string; lastName?: string; email?: string; phone?: string };
  customerFullName?: string;
  billingAddress?: Address;
  shippingAddress?: Address;
  paymentMethod?: string;
  shippingMethod?: string;
  shippingLines?: ShippingLine[];
  payments?: Payments;
  noteAttributes?: Attribute[];
  salesChannel?: string;
  sourceName?: string;
  taxCredentials?: { taxCredentialTr?: string; shippingCredentialTr?: string };
  wasEdited?: boolean;
  lastEditedAt?: { toDate: () => Date } | null;
  trackingInfo?: TrackingInfo;
  tracking?: TrackingInfo;
  productionNote?: string;
  customerNote?: string;
  internalNotes?: string;
  productionId?: string;
  productionStatus?: string;
  /** Kalemlerden biri stoktan çıkıyorsa true — sunucuda yazılır. */
  fastShipping?: boolean;
  lineItemCount?: number;
  totalQuantity?: number;
  deleted?: boolean;
}

interface ItemDoc {
  title?: string;
  variantTitle?: string;
  optionSize?: string;
  /** Kalemin TÜM özel istekleri — kumaş kodu, ayak tipi, montaj notu… */
  properties?: Attribute[];
  sku?: string;
  quantity?: number;
  /** ⚠ GÖÇ MİRASI — TL cinsinden ondalık. Shopify yolu bunu YAZMAZ. */
  unitPrice?: number;
  /** Göç mirası — KURUŞ. Shopify yolunda karşılığı `originalTotal`. */
  totalKurus?: number;
  originalTotal?: number;
  discountedTotal?: number;
  originalUnitPrice?: number;
  imageUrl?: string;
  productId?: string;
  /** Katalogdaki gerçek doküman anahtarı (ürün ya da varyant kimliği). */
  catalogProductId?: string;
  /** Pazaryerinin kendi başlığı (Trendyol/Shopify) — Balder adından farklı olabilir. */
  marketplaceTitle?: string;
  isProductDeleted?: boolean;
}

interface StatusDoc {
  label?: string;
  obsolete?: boolean;
  sortOrder?: number;
  ordering?: number;
}

interface SshDoc {
  orderId?: string;
  productTitle?: string;
  problemNote?: string;
  status?: string;
  statusLabel?: string;
  priority?: string;
  volume?: number;
}

/**
 * Sipariş bazlı maliyet dokümanı — `orders/{id}/private/finance`.
 *
 * ★ EKRAN ALTI AY BOYUNCA VAR OLMAYAN ALTI ALANI OKUDU.
 *
 * Buradaki liste şu adları arıyordu:
 *   materialCost · cargoCost · totalCost · netSale · grossProfit · marginPct
 * Bu dokümanı yazan TEK kod ise (`costing/orderCogs.ts:204-211`) bambaşka
 * adlar yazıyor: `cogsKurus` · `costedItems` · `uncostedItems` · `complete` ·
 * `computedAt`. Canlı ölçüm kesin: 26 finans dokümanının 26'sında aranan altı
 * alanın ALTISI DA YOK, yazılan beş alanın beşi de VAR.
 *
 * Hata SESSİZDİ ve fark edilmesi özellikle zordu: aşağıdaki süzgeç
 * (`typeof ... === "number"`) hiçbir alanı geçiremediği için liste boş
 * kalıyordu, doküman VAR OLDUĞU için "henüz hesaplanmadı" mesajı da
 * çıkmıyordu. Yani panel açılıyor, başlığı ve "Gizli" rozeti duruyor, içi
 * bomboş görünüyordu — hesaplanmış maliyet ise dokümanda hazır bekliyordu.
 *
 * ── NİÇİN KÂR BURADA HESAPLANMIYOR ────────────────────────────────────────
 * Sunucu bu dokümana yalnızca MALİYETİ yazıyor; kâr için KDV, komisyon ve
 * kargo parametreleri gerekir ve bunların otoritesi kârlılık raporudur
 * (`reports/profit.ts`). Aynı hesabı istemcide ikinci kez yazmak, iki rakamın
 * ayrışması demektir — bu projedeki sapmaların tam kaynağı budur. Bu yüzden
 * burada YAZILAN gösterilir, uydurulan gösterilmez.
 */
type FinanceDoc = Record<string, unknown>;

/** `unknown` alanı yalnızca gerçekten sayıysa döndürür. */
function numOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Ad-değer listesi — sipariş ve kalem özel istekleri aynı biçimde gösterilir. */
function AttributeList({ items }: { items: Attribute[] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
      {items.map((p) => (
        <div key={`${p.name}-${p.value}`} className="flex justify-between gap-3 border-b border-gray-100 py-1">
          <dt className="text-gray-500">{p.name || "—"}</dt>
          <dd className="text-right font-medium text-gray-900">{p.value || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function AddressBlock({ title, a, email, phone }: { title: string; a?: Address; email?: string; phone?: string }) {
  const lines = [
    a?.address1,
    a?.address2,
    [a?.zip, a?.city].filter(Boolean).join(" "),
    a?.province,
    a?.country,
  ].filter((x): x is string => Boolean(x && x.trim()));

  return (
    <div className="card card-pad">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {/* Kurumsal ünvan adres metnine karışmaz; e-Fatura alanı olarak ayrı durur. */}
      {a?.company?.trim() ? (
        <p className="mt-1 text-sm font-medium text-gray-900">{a.company}</p>
      ) : null}
      {lines.length === 0 && !a?.formatted ? (
        <p className="mt-2 text-sm text-gray-500">Adres bilgisi yok.</p>
      ) : (
        <address className="mt-2 not-italic text-sm text-gray-700">
          {lines.length > 0
            ? lines.map((l) => <div key={l}>{l}</div>)
            : (a?.formatted ?? "").split("\n").map((l) => <div key={l}>{l}</div>)}
        </address>
      )}
      <dl className="mt-3 space-y-1 text-sm">
        {email ? (
          <div className="flex gap-2">
            <dt className="text-gray-500">E-posta:</dt>
            <dd className="break-all text-gray-900">{email}</dd>
          </div>
        ) : null}
        {(phone ?? a?.phone) ? (
          <div className="flex gap-2">
            <dt className="text-gray-500">Telefon:</dt>
            <dd className="text-gray-900">{phone ?? a?.phone}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

export function OrderDetail() {
  const { orderId = "" } = useParams();
  const session = useSession();
  const { isStaff, seeFinance, isBoss } = useCan();
  const toast = useToast();

  const orderQ = useDoc<OrderDoc>(() => (orderId ? doc(db, "orders", orderId) : null), [orderId]);
  const order = orderQ.data;

  const itemsQ = useCollection<ItemDoc>(
    () => (orderId ? collection(db, "orders", orderId, ITEMS_SUBCOLLECTION) : null),
    [orderId],
  );

  const statusesQ = useCollection<StatusDoc>(
    () => query(collection(db, "statuses"), where("category", "==", "orders")),
    [],
  );
  const stages = useProductionStageLabels();
  const statuses = statusesQ.docs
    .filter((s) => s.obsolete !== true)
    .sort((a, b) => (a.sortOrder ?? a.ordering ?? 10000) - (b.sortOrder ?? b.ordering ?? 10000));

  // Maliyet/kâr: sorgu YALNIZCA financeView varsa açılır.
  const financeQ = useDoc<FinanceDoc>(
    () => (seeFinance && orderId ? doc(db, "orders", orderId, "private", "finance") : null),
    [seeFinance, orderId],
  );

  const sshQ = useCollection<SshDoc>(
    () =>
      orderId
        ? query(collection(db, "afterSales"), where("orderId", "==", orderId), fsLimit(25))
        : null,
    [orderId],
  );

  // `message` sunucuda YOK — gerçek şekil {status,label,results,failed}
  // (`functions/src/orders/status.ts`). Tip artık sunucunun döndürdüğü şey.
  const setStatusAction = useCallableAction<
    SetOrderErpStatusInput,
    SetOrderErpStatusResult
  >(CALLABLE_SET_STATUS);

  const trackingAction = useCallableAction<
    {
      orderId: string;
      provider: string;
      customProvider?: string;
      trackingNumber: string;
      trackingUrl?: string;
      notifyCustomer?: boolean;
    },
    /**
     * ★ SUNUCU "kaydedildi ama Shopify'a yazılamadı" diyebilir ve bu bir HATA
     * DEĞİL, KISMİ BAŞARIDIR (kayıt yazıldı, müşteriye bildirim gitmedi).
     * Ekran yalnızca `message` okuyup her sonucu YEŞİL kutuda gösteriyordu:
     * "müşteriye kargo bildirimi GİTMEDİ" cümlesi başarı rengiyle geçiyordu.
     */
    {
      message?: string;
      shopifySynced?: boolean;
      shopifyState?: string;
      shopifyError?: string;
      /** Sipariş bu kayıtla "Kargoya Verildi"ye geçtiyse true. */
      movedToShipped?: boolean;
    }
  >(CALLABLE_SAVE_TRACKING);

  /**
   * Sipariş detayı (üretim notu dahil) — sunucudaki alan beyaz listesinden ve
   * denetim kaydından geçen TEK yol.
   */
  const detailsAction = useCallableAction<
    { orderId: string; productionNote?: string },
    { changedFields?: string[] }
  >(CALLABLE_UPDATE_DETAILS);

  const retryFulfillmentAction = useCallableAction<
    { orderIds: string[] },
    { succeeded?: number; failed?: number; message?: string; results?: Array<{ orderId: string; state: string; error?: string }> }
  >(CALLABLE_RETRY_FULFILLMENT);

  /**
   * ★ CANLI KARGO TAKİBİ. Takip numarası bugüne kadar yalnızca bir bağlantıydı;
   * "teslim edildi mi" sorusu için ofis UPS sitesinde tek tek arıyordu.
   */
  const trackAction = useCallableAction<
    { orderId: string },
    {
      trackingNumber: string;
      status: string;
      statusLabel: string;
      description: string;
      deliveredAt: string | null;
      events: Array<{ at: string | null; status: string; description: string; location: string }>;
      /** Teslim bilgisi siparişi "Tamamlandı"ya çektiyse true. */
      completed?: boolean;
    }
  >(CALLABLE_TRACK_SHIPMENT);

  const onTrack = async () => {
    const out = await trackAction.run({ orderId });
    if (!out) {
      toast.error(trackAction.error ?? "Kargo durumu sorgulanamadı.");
      return;
    }
    if (out.status === "delivered") {
      toast.success(
        `Teslim edildi${out.deliveredAt ? ` — ${date(out.deliveredAt)}` : ""}.`,
        out.completed ? 'Sipariş "Tamamlandı" statüsüne alındı.' : undefined,
      );
    } else {
      toast.info(out.statusLabel, out.description || undefined);
    }
  };

  const [targetStatus, setTargetStatus] = useState("");
  const [note, setNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteDirty, setNoteDirty] = useState(false);

  /**
   * ★ KARGO FORMU DENETİMLİ HÂLE GETİRİLDİ.
   *
   * Üç kutu da `defaultValue` ile duruyordu, yani birbirinden habersizdiler:
   *   · "Özel sağlayıcı adı" UPS seçiliyken de ekranda duruyordu ve doldurulsa
   *     bile hiçbir işe yaramıyordu — kullanıcıya ölü bir kutu gibi görünüyordu.
   *   · "Takip bağlantısı" kaydetmeden önce BOŞ kalıyordu; adres kaydedildikten
   *     sonra sunucuda üretiliyordu. Kutuyu boş gören kullanıcı alanın
   *     çalışmadığını düşünüyordu.
   * Artık firma ve numara duruma bağlı: özel ad yalnızca "Özel Sağlayıcı"
   * seçildiğinde çıkar, bağlantı ise yazdıkça firma kalıbından ÖNİZLENİR.
   * Kullanıcı bağlantıya elle dokunursa (`urlTouched`) otomatik üretim durur —
   * elle yazılan adresin üstüne yazmak, en can sıkıcı hata türüdür.
   */
  const [formProvider, setFormProvider] = useState(DEFAULT_CARGO_PROVIDER.label);
  const [formCustomProvider, setFormCustomProvider] = useState("");
  const [formNumber, setFormNumber] = useState("");
  const [formUrl, setFormUrl] = useState("");
  const [urlTouched, setUrlTouched] = useState(false);

  useEffect(() => {
    if (order) {
      setTargetStatus(order.erpStatus ?? "");
      setNote(order.productionNote ?? "");
      setNoteDirty(false);
    }
  }, [order?.erpStatus, order?.productionNote]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Sipariş yüklenince / kargo bilgisi değişince formu kayıttan tazeler. */
  useEffect(() => {
    if (!order) return;
    const t: TrackingInfo = {
      ...(order.tracking ?? {}),
      ...Object.fromEntries(
        Object.entries(order.trackingInfo ?? {}).filter(([, v]) => v !== "" && v != null),
      ),
    };
    const number = t.trackingNumber ?? t.number ?? "";
    const stored = t.provider ?? t.company ?? DEFAULT_CARGO_PROVIDER.label;
    // Kayıtlı ad tanınmıyorsa numaraya bakılır (`1Z…` = UPS); o da tutmazsa
    // gerçekten özel bir sağlayıcıdır ve ad kutusu açılır.
    const resolved = resolveProvider(stored, number);
    setFormProvider(resolved);
    // Yer tutucu metni ("Özel Sağlayıcı") firma adı diye kutuya yazılmaz.
    const custom = t.customProvider ?? stored;
    setFormCustomProvider(resolved === "" && !isPlaceholderProvider(custom) ? custom : "");
    setFormNumber(number);
    setFormUrl(t.trackingUrl ?? t.url ?? "");
    setUrlTouched(Boolean(t.trackingUrl ?? t.url));
  }, [order?.tracking, order?.trackingInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Firma ya da numara değiştikçe bağlantıyı tazeler (elle yazılmadıysa). */
  useEffect(() => {
    if (urlTouched) return;
    setFormUrl(buildTrackingUrl(formProvider, formNumber) ?? "");
  }, [formProvider, formNumber, urlTouched]);

  if (!orderId) return <EmptyState title="Sipariş numarası eksik" />;
  if (orderQ.loading) return <LoadingBlock label="Sipariş yükleniyor" />;
  if (orderQ.error) {
    return (
      <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
        {orderQ.error}
      </div>
    );
  }
  if (orderQ.notFound || !order) {
    return (
      <EmptyState
        title="Sipariş bulunamadı"
        description={`"${orderId}" numaralı sipariş kaydı yok. Numara yanlış olabilir.`}
        action={
          <Link to="/orders" className="btn-secondary">
            Sipariş listesine dön
          </Link>
        }
      />
    );
  }

  /**
   * ★ İKİ MAP BİRLEŞTİRİLİR, BİRİ SEÇİLMEZ.
   *
   * Eskiden `order.trackingInfo ?? order.tracking` yazıyordu: `trackingInfo`
   * VARSA — içi boş olsa bile — `tracking` hiç okunmuyordu. Shopify kargo
   * webhook'u yalnızca `tracking`'e yazdığı için, ERP'den bir kez kargo formu
   * açılmış siparişte Shopify'ın getirdiği takip numarası GÖRÜNMEZ oluyordu.
   * Sunucu artık iki adı birlikte yazıyor (`orders/contract.ts`); burada da
   * eski kayıtlar için iki map alan alan birleştirilir, dolu olan kazanır.
   */
  const tracking: TrackingInfo = {
    ...(order.tracking ?? {}),
    ...Object.fromEntries(
      Object.entries(order.trackingInfo ?? {}).filter(([, v]) => v !== "" && v != null),
    ),
  };
  const noteAttributes = (order.noteAttributes ?? []).filter((p) => p.name || p.value);
  const payments = order.payments;
  const shippingLines = order.shippingLines ?? [];
  const shippingFee = shippingLines.reduce(
    (s, l) => s + (l.discountedPrice ?? l.price ?? 0),
    0,
  );
  const taxCredential =
    order.taxCredentials?.taxCredentialTr || order.taxCredentials?.shippingCredentialTr || "";
  /**
   * Orijinal (sözleşme) tutar ile güncel (iade/düzenleme sonrası) tutar
   * FARKLIYSA ikisi de gösterilir. Tek sayı göstermek, muhasebe ile Shopify
   * admin arasındaki farkın nedenini görünmez kılıyordu.
   */
  const currentTotal = order.totals?.currentTotal;
  const totalsDiffer =
    typeof currentTotal === "number" &&
    typeof order.totals?.total === "number" &&
    currentTotal !== order.totals.total;
  /**
   * Havale indirimi Shopify'a girmediği için `totals.discount` onu içermez;
   * ekranda tek bir "İndirim" rakamı görünsün diye burada toplanır.
   */
  /**
   * ★ ESKİ SİSTEM SİPARİŞİNDE HAVALE İNDİRİMİ YOK. WooCommerce tutarı zaten
   * indirimliydi; bir onarım betiği yine de indirim alanı yazdı ve ekran
   * "Tahsil edilecek" tutarını İKİNCİ KEZ indirimli gösteriyordu (#77497:
   * 7.101 ₺ yerine 6.390,90 ₺). Ciro kuralıyla aynı (`finance/revenue.ts`).
   */
  const bankDiscount = order.channel === "legacy" ? 0 : (order.totals?.bankTransferDiscount ?? 0);
  const totalDiscount = (order.totals?.discount ?? 0) + bankDiscount;

  const currentProvider = tracking.provider ?? tracking.company ?? DEFAULT_CARGO_PROVIDER.label;
  const currentNumber = tracking.trackingNumber ?? tracking.number ?? "";

  const onChangeStatus = async () => {
    if (!targetStatus || targetStatus === order.erpStatus) return;
    const out = await setStatusAction.run({ orderIds: [orderId], status: targetStatus });
    if (!out) {
      toast.error(setStatusAction.error ?? "Statü değiştirilemedi.");
      return;
    }
    // Tek sipariş: hepsi başarısızsa sunucu zaten fırlatır, ama sebebi yine de
    // `results`tan okuruz — sessiz kısmi başarı bırakmayalım.
    const row = out.results?.[0];
    if (row && !row.ok) {
      toast.error("Sipariş statüsü değiştirilemedi.", row.error ?? undefined);
      return;
    }
    toast.success(`Sipariş statüsü "${out.label}" olarak güncellendi.`);
  };

  /**
   * ★ ÜRETİM NOTU ARTIK CALLABLE ÜZERİNDEN KAYDEDİLİR.
   *
   * Burası `updateDoc` ile doğrudan Firestore'a yazıyordu. Sunucuda AYNI işi
   * yapan `updateOrderDetails` ucu zaten vardı ve doğrudan yazmanın atladığı üç
   * şeyi yapıyor:
   *   · alan beyaz listesi + uzunluk doğrulaması (Rules yalnızca "hangi alana
   *     dokunulabilir"e bakar, içeriğe bakmaz),
   *   · `audit()` kaydı — notu kimin ne zaman değiştirdiği izlenebilir kalır,
   *   · Shopify'a yansıtılması gereken alan varsa `shopifySyncPending` işareti.
   * Doğrudan yazma bunların hiçbirini yapmıyordu ve hata da vermiyordu.
   */
  const onSaveNote = async () => {
    if (!session) return;
    setNoteSaving(true);
    try {
      const out = await detailsAction.run({ orderId, productionNote: note });
      if (!out) {
        toast.error(detailsAction.error ?? "Üretim notu kaydedilemedi.");
        return;
      }
      setNoteDirty(false);
      toast.success("Üretim notu kaydedildi.");
    } finally {
      setNoteSaving(false);
    }
  };

  const onSaveTracking = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const provider = String(form.get("provider") ?? "");
    const customProvider = String(form.get("customProvider") ?? "").trim();
    const trackingNumber = String(form.get("trackingNumber") ?? "").trim();
    const trackingUrl = String(form.get("trackingUrl") ?? "").trim();
    const notifyCustomer = form.get("notifyCustomer") === "on";

    // Eski `save_shipment_info()` takip numarası boşsa SESSİZCE hiçbir şey
    // kaydetmiyordu. Artık kullanıcı neden kaydedilmediğini görüyor.
    if (!trackingNumber) {
      toast.error("Takip numarası zorunludur; boş bırakılırsa kargo bilgisi kaydedilmez.");
      return;
    }

    /**
     * ★ "Özel Sağlayıcı" seçilip ad yazılmazsa firma adı olarak Shopify'a
     * LİTERAL "Özel Sağlayıcı" gidiyordu: müşteri kargo e-postasında firma adı
     * yerine bu ifadeyi görüyordu. Sevk kuyruğu ekranı bu kontrolü zaten
     * yapıyordu, sipariş detayı yapmıyordu.
     */
    if (!provider && !customProvider) {
      toast.error("Kargo firmasını seçin veya özel sağlayıcı adını yazın.");
      return;
    }

    const payload: {
      orderId: string;
      provider: string;
      customProvider?: string;
      trackingNumber: string;
      trackingUrl?: string;
      notifyCustomer?: boolean;
    } = {
      orderId,
      provider: provider || customProvider,
      trackingNumber,
      notifyCustomer,
    };
    if (customProvider) payload.customProvider = customProvider;
    /**
     * ★ TAKİP BAĞLANTISI BOŞ BIRAKILIRSA FİRMA KALIBINDAN ÜRETİLİR.
     * Shopify takip linkini yalnızca KENDİ TANIDIĞI taşıyıcılar için üretir ve
     * Türk kargo firmalarının hiçbirini tanımaz. Bu kutu isteğe bağlı olduğu
     * için pratikte boş bırakılıyor, müşteriye giden e-postada tıklanabilir
     * takip bağlantısı hiç çıkmıyordu. (Sevk kuyruğu ekranı bunu yapıyordu.)
     */
    const url = trackingUrl || buildTrackingUrl(provider, trackingNumber) || "";
    if (url) payload.trackingUrl = url;

    const out = await trackingAction.run(payload);
    if (!out) {
      toast.error(trackingAction.error ?? "Kargo bilgisi kaydedilemedi.");
      return;
    }
    // Statü değişimi ayrıca söylenir: sipariş artık "Kargoya Verildi"
    // sekmesindedir ve kullanıcı onu bulunduğu yerde aramasın.
    const moved = out.movedToShipped ? ' Sipariş "Kargoya Verildi"ye alındı.' : "";
    // Kısmi başarı kırmızıyla gösterilir; sebep sunucudan AYNEN gelir.
    if (out.shopifySynced === false && out.shopifyState !== "skipped") {
      toast.error(
        "Takip numarası kaydedildi ama Shopify'a yazılamadı — müşteriye bildirim GİTMEDİ." + moved,
        out.shopifyError ?? out.message ?? undefined,
      );
      return;
    }
    if (out.shopifyState === "skipped") {
      toast.info((out.message ?? "Takip numarası kaydedildi.") + moved);
      return;
    }
    toast.success((out.message ?? "Kargo bilgisi kaydedildi.") + moved);
  };

  /** Shopify'a yazılamamış kaydı yeniden gönderir (izin/kapsam düzeldikten sonra). */
  const onRetryFulfillment = async () => {
    const out = await retryFulfillmentAction.run({ orderIds: [orderId] });
    if (!out) {
      toast.error(retryFulfillmentAction.error ?? "Yeniden gönderilemedi.");
      return;
    }
    const first = out.results?.[0];
    if (first?.state === "ok") {
      toast.success("Kargo bilgisi Shopify'a yazıldı; müşteriye bildirim gitti.");
      return;
    }
    toast.error("Shopify'a yine yazılamadı.", first?.error ?? out.message ?? undefined);
  };

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div>
        <nav aria-label="Sayfa yolu" className="noprint text-sm">
          <ol role="list" className="flex items-center gap-2 text-gray-500">
            <li>
              <Link to="/orders" className="hover:text-gray-700">
                Siparişler
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-700">Sipariş detayı</li>
          </ol>
        </nav>

        <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-[28px] sm:leading-9">
              {order.orderName ?? orderId}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600">
              <ChannelBadge channel={channelOf(order)} size="md" />
              <OrderStatusBadge
                erpStatus={order.erpStatus}
                erpStatusLabel={order.erpStatusLabel}
                productionStatus={order.productionStatus}
                stages={stages}
                size="md"
              />
              <FastShippingBadge fastShipping={order.fastShipping} size="md" />
              <span>{dt(order.createdAt)}</span>
              {order.financialStatus ? <span>· Ödeme: {financialLabel(order.financialStatus)}</span> : null}
              {order.fulfillmentStatus ? <span>· Gönderim: {order.fulfillmentStatus}</span> : null}
            </div>
          </div>

          <div className="noprint flex flex-wrap items-center gap-2">
            <button type="button" className="btn-secondary" onClick={() => window.print()}>
              Yazdır
            </button>
            {order.productionId ? (
              <Link to={`/production/${order.productionId}`} className="btn-secondary">
                Üretim kaydı
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      {/* Sipariş Shopify'da sonradan düzenlendiyse atölye eski kesim listesiyle
          çalışıyor olabilir — bu uyarı yazdırılan sayfada da görünür. */}
      {order.wasEdited ? (
        <p role="status" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Bu siparişin içeriği Shopify'da düzenlendi
          {order.lastEditedAt ? ` (${dt(order.lastEditedAt)})` : ""}. Kesim listesini ve
          üretim notunu güncel kalemlere göre kontrol edin.
        </p>
      ) : null}

      {!isStaff ? (
        <p className="noprint rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">
          Bu ekran sizin için salt okunurdur. Sipariş tutarını görebilirsiniz; statü,
          kargo ve not düzenlemesini ofis veya iş sahibi yapar.
        </p>
      ) : null}

      {/* Özet + tutarlar */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="card card-pad lg:col-span-2">
          <h2 className="text-sm font-semibold text-gray-900">Sipariş bilgileri</h2>
          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <div className="flex justify-between gap-4 border-b border-gray-100 py-1">
              <dt className="text-gray-500">Müşteri</dt>
              <dd className="text-right font-medium text-gray-900">
                {(order.customerFullName ??
                  `${order.customer?.firstName ?? ""} ${order.customer?.lastName ?? ""}`.trim()) ||
                  "—"}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-gray-100 py-1">
              <dt className="text-gray-500">Ödeme yöntemi</dt>
              <dd className="text-right text-gray-900">{order.paymentMethod ? paymentLabel(order.paymentMethod).label : "—"}</dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-gray-100 py-1">
              <dt className="text-gray-500">Gönderim yöntemi</dt>
              <dd className="text-right text-gray-900">
                {order.shippingMethod?.trim() ? order.shippingMethod : "—"}
                {shippingFee > 0 ? (
                  <span className="ml-1 text-xs text-gray-500">({tl(shippingFee)})</span>
                ) : null}
              </dd>
            </div>
            {/* Başlıktaki rozet aşamayı yalnızca sipariş üretimdeyken yazar. Bu
                satır her durumda görünür: kargoya verilmiş ama üretim kaydı
                "Üretimi Bekleyen"de kalmış sipariş (canlıda 6 adet) buradan fark edilir. */}
            {order.productionStatus ? (
              <div className="flex justify-between gap-4 border-b border-gray-100 py-1">
                <dt className="text-gray-500">Üretim aşaması</dt>
                <dd className="text-right font-medium text-gray-900">
                  {stageLabelOf(stages, order.productionStatus) ?? "…"}
                </dd>
              </div>
            ) : null}
            {order.salesChannel || order.sourceName ? (
              <div className="flex justify-between gap-4 border-b border-gray-100 py-1">
                <dt className="text-gray-500">Satış kanalı</dt>
                <dd className="text-right text-gray-900">
                  {order.salesChannel || order.sourceName}
                </dd>
              </div>
            ) : null}
            {taxCredential ? (
              <div className="flex justify-between gap-4 border-b border-gray-100 py-1">
                <dt className="text-gray-500">TCKN / VKN</dt>
                <dd className="text-right tabular-nums text-gray-900">{taxCredential}</dd>
              </div>
            ) : null}
            <div className="flex justify-between gap-4 border-b border-gray-100 py-1">
              <dt className="text-gray-500">Kalem / adet</dt>
              <dd className="text-right text-gray-900">
                {n(order.lineItemCount)} / {n(order.totalQuantity)}
              </dd>
            </div>
            {order.erpStatusChangedAt ? (
              <div className="flex justify-between gap-4 border-b border-gray-100 py-1">
                <dt className="text-gray-500">Son statü değişimi</dt>
                <dd className="text-right text-gray-900">{dt(order.erpStatusChangedAt)}</dd>
              </div>
            ) : null}
            {order.cancelledAt ? (
              <div className="flex justify-between gap-4 border-b border-gray-100 py-1">
                <dt className="text-gray-500">İptal tarihi</dt>
                <dd className="text-right text-gray-900">{dt(order.cancelledAt)}</dd>
              </div>
            ) : null}
          </dl>
        </div>

        {/* Tutarlar — ÜÇ ROLE DE AÇIK (yetki matrisi §2.2) */}
        <div className="card card-pad">
          <h2 className="text-sm font-semibold text-gray-900">Tutarlar</h2>
          <dl className="mt-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Ara toplam</dt>
              <dd className="tabular-nums text-gray-900">{tl(order.totals?.subtotal)}</dd>
            </div>
            {/* ★ İNDİRİM — Shopify'ın indirimi + havale indirimi TEK SATIRDA.
                Havale indirimi Shopify'a hiç girmiyor (Money Order siparişlerinin
                tamamında `discount: 0` geliyor), o yüzden burada eklenmezse ekran
                "indirim yok" der ve müşterinin fiilen ödediğinden fazlası görünür. */}
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">
                İndirim
                {bankDiscount > 0 ? (
                  <span className="ml-1 text-xs text-amber-800">
                    (havale %{order.totals?.bankTransferDiscountPercent ?? 10})
                  </span>
                ) : null}
              </dt>
              <dd
                className={`shrink-0 tabular-nums ${
                  bankDiscount > 0 ? "font-medium text-amber-800" : "text-gray-900"
                }`}
              >
                {totalDiscount > 0 ? "−" : ""}
                {tl(totalDiscount)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Kargo</dt>
              <dd className="tabular-nums text-gray-900">{tl(order.totals?.shipping)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Vergi</dt>
              <dd className="tabular-nums text-gray-900">{tl(order.totals?.tax)}</dd>
            </div>
            {order.totals?.refunded ? (
              <div className="flex justify-between">
                <dt className="text-gray-500">İade</dt>
                <dd className="tabular-nums text-gray-900">{tl(order.totals.refunded)}</dd>
              </div>
            ) : null}
            {/* ★ TOPLAM — havale indiriminde TAHSİL EDİLECEK tutar gösterilir.
                Shopify'ın kaydettiği tutar EZİLMEZ, altına ayrı satır olarak
                konur: mutabakatın dayanağı odur, ama ekrandaki büyük rakam
                kasaya girecek para olmalı. */}
            <div className="mt-2 flex justify-between gap-3 border-t border-gray-200 pt-2 text-base font-semibold">
              <dt>{bankDiscount > 0 ? "Tahsil edilecek" : "Toplam"}</dt>
              <dd className="shrink-0 tabular-nums">
                {tl(bankDiscount > 0 ? order.totals?.effectiveTotal : order.totals?.total)}
              </dd>
            </div>
            {bankDiscount > 0 ? (
              <div className="flex justify-between gap-3 text-xs">
                <dt className="text-gray-600">Shopify'daki tutar (indirimsiz)</dt>
                <dd className="shrink-0 tabular-nums text-gray-600">{tl(order.totals?.total)}</dd>
              </div>
            ) : null}
            {totalsDiffer ? (
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Güncel toplam (iade/düzenleme sonrası)</dt>
                <dd className="tabular-nums font-medium text-gray-900">{tl(currentTotal)}</dd>
              </div>
            ) : null}

            {/* Kapora / bakiye — havale ile satışta sevk kararının dayanağı. */}
            {payments && (payments.received || payments.outstanding) ? (
              <div className="mt-2 border-t border-gray-200 pt-2">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Tahsil edilen</dt>
                  <dd className="tabular-nums text-gray-900">{tl(payments.received)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Kalan bakiye</dt>
                  <dd
                    className={`tabular-nums font-semibold ${
                      (payments.outstanding ?? 0) > 0 ? "text-red-700" : "text-green-700"
                    }`}
                  >
                    {tl(payments.outstanding)}
                  </dd>
                </div>
              </div>
            ) : null}
          </dl>
        </div>
      </div>

      {/* Kalemler — siparişin özü, özetin hemen altında (eskiden sayfanın en dibindeydi) */}
      <section aria-labelledby="kalem-baslik">
        <h2 id="kalem-baslik" className="text-sm font-semibold text-gray-900">
          Sipariş kalemleri
        </h2>
        <p className="noprint mt-1 text-xs text-gray-500">
          Kalem tutarları Shopify otoritesindedir; ERP'den değiştirilemez.
        </p>

        {itemsQ.loading ? (
          <LoadingBlock />
        ) : itemsQ.error ? (
          <div role="alert" className="mt-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
            {itemsQ.error}
          </div>
        ) : itemsQ.docs.length === 0 ? (
          <div className="mt-2">
            <EmptyState title="Bu siparişte kalem kaydı yok" tone="warning" />
          </div>
        ) : (
          <div className="mt-2 overflow-x-auto rounded-lg bg-white ring-1 ring-gray-200">
            <table className="table-dense">
              <caption className="sr-only">Sipariş kalemleri</caption>
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col">Ürün</th>
                  <th scope="col" className="hidden sm:table-cell">
                    SKU
                  </th>
                  <th scope="col" className="text-right">
                    Adet
                  </th>
                  <th scope="col" className="hidden text-right sm:table-cell">
                    Birim
                  </th>
                  <th scope="col" className="hidden text-right sm:table-cell">
                    Toplam
                  </th>
                  <th scope="col" className="hidden text-right sm:table-cell">
                    İndirim
                  </th>
                  <th scope="col" className="text-right">
                    İndirimli
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {itemsQ.docs.map((it) => {
                  // ★ İKİ ŞEMA TEK EKRANDA.
                  // Göç edilmiş 14.606 siparişin kalemleri `totalKurus` (KURUŞ)
                  // ve `unitPrice` (TL, ondalıklı) yazar; Shopify yolu ise
                  // `originalTotal`/`discountedTotal` (KURUŞ) yazar. Ekran
                  // yalnızca Shopify adlarını okuyup `unitPrice`'ı da kuruş
                  // sanıyordu: ₺21.773,00'lik kalem ₺217,73 görünüyor, TOPLAM ve
                  // İNDİRİMLİ sütunları da boş kalıyordu.
                  const lineTotal = it.discountedTotal ?? it.originalTotal ?? it.totalKurus ?? null;
                  const lineOriginal = it.originalTotal ?? it.totalKurus ?? null;
                  const qty = Number(it.quantity) || 0;
                  const unitKurus =
                    it.originalUnitPrice ??
                    (lineOriginal !== null && qty > 0 ? Math.round(lineOriginal / qty) : null);
                  const discount =
                    it.originalTotal !== undefined && it.discountedTotal !== undefined
                      ? it.originalTotal - it.discountedTotal
                      : null;
                  return (
                    <tr key={it.id}>
                      <td>
                        <div className="flex items-center gap-2">
                          {it.imageUrl ? (
                            <img
                              src={it.imageUrl}
                              alt=""
                              loading="lazy"
                              className="h-10 w-10 shrink-0 rounded object-cover"
                            />
                          ) : null}
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900">
                              {it.productId && !it.isProductDeleted ? (
                                <Link
                                  to={`/products/${it.catalogProductId || it.productId}`}
                                  className="link"
                                >
                                  {it.title ?? "Ürün"}
                                </Link>
                              ) : (
                                (it.title ?? "Ürün")
                              )}
                            </p>
                            {it.variantTitle || it.optionSize ? (
                              <p className="text-xs text-gray-600">
                                {[it.variantTitle, it.optionSize].filter(Boolean).join(" · ")}
                              </p>
                            ) : null}
                            {/* ★ PAZARYERİ BAŞLIĞI İKİNCİL SATIRDA.
                                Trendyol'un pazarlama başlığı Balder'in adından
                                çok farklıdır ("Stork Masif Kayın Ahşap Zigon
                                Sehpa Yan Sehpa Doğal 6398, one size"). Ekip
                                Balder'in adıyla çalışır, ama müşteri destek
                                aradığında Trendyol'daki adı söyler — o yüzden
                                ikisi de görünür kalmalı. Aynıysa tekrarlanmaz. */}
                            {it.marketplaceTitle && it.marketplaceTitle !== it.title ? (
                              <p className="mt-0.5 text-xs text-gray-600">
                                <span className="text-gray-500">Pazaryerinde:</span>{" "}
                                {it.marketplaceTitle}
                              </p>
                            ) : null}
                            {/* Kalem özel istekleri (kumaş kodu, ayak tipi…):
                                kesim listesinin girdisi, gizlenmemeli. */}
                            {(it.properties ?? []).length > 0 ? (
                              <ul className="mt-1 space-y-0.5">
                                {(it.properties ?? []).map((p) => (
                                  <li key={`${p.name}-${p.value}`} className="text-xs text-amber-800">
                                    <span className="text-gray-500">{p.name}:</span> {p.value}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                            {it.isProductDeleted ? (
                              <p className="text-xs text-amber-700">
                                Bu ürün artık katalogda yok (üretimden kalkmış) — sipariş kaydı olduğu gibi korunuyor
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell">{it.sku ?? "—"}</td>
                      <td className="text-right tabular-nums">{n(it.quantity)}</td>
                      <td className="hidden text-right tabular-nums sm:table-cell">
                        {unitKurus === null ? "—" : tl(unitKurus)}
                      </td>
                      <td className="hidden text-right tabular-nums sm:table-cell">
                        {lineOriginal === null ? "—" : tl(lineOriginal)}
                      </td>
                      <td className="hidden text-right tabular-nums sm:table-cell">
                        {discount === null ? "—" : tl(discount)}
                      </td>
                      <td className="text-right tabular-nums">{lineTotal === null ? "—" : tl(lineTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Ödeme hareketleri — kapora kart, bakiye havale olabilir. */}
      {payments?.transactions?.length ? (
        <section className="card card-pad" aria-labelledby="odeme-baslik">
          <h2 id="odeme-baslik" className="text-sm font-semibold text-gray-900">
            Ödeme hareketleri
          </h2>
          <ul role="list" className="mt-2 divide-y divide-gray-200 text-sm">
            {payments.transactions.map((t) => (
              <li key={t.transactionId} className="flex items-center justify-between gap-4 py-2">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">
                    {t.gateway || "—"}
                    {t.manual ? " (havale/elle)" : ""}
                  </p>
                  <p className="text-xs text-gray-500">
                    {[t.kind, t.status, dt(t.processedAt)].filter(Boolean).join(" · ")}
                  </p>
                </div>
                {/*
                  İndirimli tutar VARSA o basılır; Shopify'ın indirimsiz rakamı
                  altında küçük punto ile kalır. İkisini birden göstermek şart:
                  banka ekstresiyle eşleşen rakam üstteki, Shopify panelinde
                  görünen ise alttaki.
                */}
                <span className="shrink-0 text-right">
                  <span className="block tabular-nums font-medium text-gray-900">
                    {tl(t.effectiveAmount ?? t.amount)}
                  </span>
                  {t.effectiveAmount !== undefined && t.effectiveAmount !== t.amount ? (
                    <span className="block text-xs tabular-nums text-gray-500">
                      Shopify: {tl(t.amount)}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Maliyet / kâr — YALNIZCA financeView */}
      {seeFinance ? (
        <section className="card card-pad" aria-labelledby="finans-baslik">
          <div className="flex items-center gap-2">
            <h2 id="finans-baslik" className="text-sm font-semibold text-gray-900">
              Maliyet ve kâr
            </h2>
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold uppercase text-amber-900">
              Gizli
            </span>
          </div>
          {financeQ.loading ? (
            <LoadingBlock />
          ) : financeQ.error ? (
            <p role="alert" className="mt-2 text-sm text-red-800">
              {financeQ.error}
            </p>
          ) : financeQ.notFound || !financeQ.data ? (
            <p className="mt-2 text-sm text-gray-500">
              Bu sipariş için maliyet henüz hesaplanmadı.
            </p>
          ) : (
            (() => {
              // Sunucunun GERÇEKTEN yazdığı alanlar (bkz. FinanceDoc yorumu).
              const d = financeQ.data;
              const cogs = numOrNull(d.cogsKurus);
              const costed = numOrNull(d.costedItems);
              const uncosted = numOrNull(d.uncostedItems);
              const complete = d.complete === true;

              if (cogs === null) {
                return (
                  <p className="mt-2 text-sm text-gray-500">
                    Bu sipariş için maliyet henüz hesaplanmadı.
                  </p>
                );
              }

              return (
                <>
                  <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                    <div className="flex justify-between border-b border-gray-100 py-1">
                      <dt className="text-gray-500">Ürün maliyeti (COGS)</dt>
                      <dd className="tabular-nums font-medium text-gray-900">{tl(cogs)}</dd>
                    </div>
                    <div className="flex justify-between border-b border-gray-100 py-1">
                      <dt className="text-gray-500">Maliyeti bilinen kalem</dt>
                      <dd className="tabular-nums text-gray-900">
                        {n(costed)}
                        {uncosted !== null && uncosted > 0 ? ` / ${n((costed ?? 0) + uncosted)}` : null}
                      </dd>
                    </div>
                    {d.computedAt ? (
                      <div className="flex justify-between border-b border-gray-100 py-1">
                        <dt className="text-gray-500">Hesaplandığı an</dt>
                        <dd className="tabular-nums text-gray-900">{dt(d.computedAt as never)}</dd>
                      </div>
                    ) : null}
                  </dl>

                  {/* ★ EKSİK MALİYET SIFIR GİBİ GÖSTERİLMEZ. Kapsam tamsa da
                      açıkça yazılır: rakamın neyi kapsadığı rakam kadar önemli. */}
                  {complete ? (
                    <p className="mt-2 text-xs text-gray-500">
                      Siparişteki tüm kalemlerin maliyeti hesaba katıldı.
                    </p>
                  ) : (
                    <p className="mt-2 text-xs font-medium text-amber-700">
                      {uncosted !== null && uncosted > 0
                        ? `${n(uncosted)} kalemin maliyeti bilinmiyor — yukarıdaki tutar EKSİKTİR.`
                        : "Maliyet kapsamı tam değil — yukarıdaki tutar eksik olabilir."}
                    </p>
                  )}

                  {/* Kâr/marj bu ekranda hesaplanmaz; otoritesi kârlılık raporudur.
                      Bağlantı yalnızca oraya GERÇEKTEN girebilene gösterilir:
                      rapor ucu `boss` + `financeView` ister, `seeFinance` tek
                      başına yetmez (bkz. App.tsx rapor rotaları). */}
                  {isBoss ? (
                    <p className="mt-2 text-xs text-gray-500">
                      Kâr ve marj kırılımı için{" "}
                      <Link to="/reports/profit" className="link">
                        Ürün Kâr Tablosu
                      </Link>{" "}
                      raporunu kullanın.
                    </p>
                  ) : null}
                </>
              );
            })()
          )}
        </section>
      ) : null}

      {/* Statü + notlar */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {isStaff ? (
          <section className="card card-pad noprint" aria-labelledby="statu-baslik">
            <h2 id="statu-baslik" className="text-sm font-semibold text-gray-900">
              Statü değiştir
            </h2>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div className="min-w-[12rem] flex-1">
                <label htmlFor="order-status" className="label">
                  Yeni statü
                </label>
                <select
                  id="order-status"
                  className="select mt-1"
                  value={targetStatus}
                  onChange={(e) => setTargetStatus(e.target.value)}
                  disabled={setStatusAction.pending}
                >
                  {statuses.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label ?? s.id}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="btn-danger"
                onClick={() => void onChangeStatus()}
                disabled={setStatusAction.pending || targetStatus === order.erpStatus}
              >
                {setStatusAction.pending ? "Kaydediliyor…" : "Statüyü değiştir"}
              </button>
            </div>
            {setStatusAction.error ? (
              <p role="alert" className="mt-2 text-sm text-red-800">
                {setStatusAction.error}
              </p>
            ) : null}
          </section>
        ) : null}

        <section className="card card-pad" aria-labelledby="not-baslik">
          <h2 id="not-baslik" className="text-sm font-semibold text-gray-900">
            Notlar
          </h2>

          <div className="mt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Müşteri notu
            </p>
            <p className="mt-1 whitespace-pre-line rounded-md bg-primary-50 p-3 text-sm text-gray-800">
              {order.customerNote?.trim() ? order.customerNote : "—"}
            </p>
          </div>

          {/* ÖZEL İSTEKLER — checkout form alanları (note_attributes).
              Kumaş kodu, ahşap rengi, ayak tipi, kat/asansör bilgisi buraya düşer;
              atölye görmezse yanlış ürün üretilir. */}
          {noteAttributes.length > 0 ? (
            <div className="mt-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Özel istekler
              </p>
              <div className="mt-1 rounded-md bg-amber-50 p-3">
                <AttributeList items={noteAttributes} />
              </div>
            </div>
          ) : null}

          <div className="mt-4">
            <label htmlFor="production-note" className="label">
              Üretim notu
            </label>
            {isStaff ? (
              <>
                <textarea
                  id="production-note"
                  className="input mt-1 min-h-[6rem]"
                  value={note}
                  maxLength={2000}
                  onChange={(e) => {
                    setNote(e.target.value);
                    setNoteDirty(true);
                  }}
                  disabled={noteSaving}
                />
                <div className="noprint mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => void onSaveNote()}
                    disabled={!noteDirty || noteSaving}
                  >
                    {noteSaving ? "Kaydediliyor…" : "Notu kaydet"}
                  </button>
                  {noteSaving ? <Spinner size="sm" label="Kaydediliyor" /> : null}
                  <span className="text-xs text-gray-500">{note.length}/2000</span>
                </div>
              </>
            ) : (
              <p className="mt-1 whitespace-pre-line rounded-md bg-gray-100 p-3 text-sm text-gray-800">
                {order.productionNote?.trim() ? order.productionNote : "—"}
              </p>
            )}
          </div>
        </section>
      </div>

      {/* Kargo bilgileri */}
      <section className="card card-pad" aria-labelledby="kargo-baslik">
        <h2 id="kargo-baslik" className="text-sm font-semibold text-gray-900">
          Kargo bilgileri
        </h2>

        {/* ── CANLI KARGO DURUMU (UPS) ────────────────────────────────────
            Taşıyıcıdan gelen veri `tracking` haritasında; ERP formunun yazdığı
            alanlarla (`trackingInfo`) karışmaması için ayrı tutulur. Üç saatte
            bir otomatik tazelenir, buradan elle de sorgulanabilir. */}
        {currentNumber ? (
          <div className="mt-3 rounded-md border border-gray-300 bg-gray-50 px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  Kargo durumu:{" "}
                  {tracking.carrierStatusLabel ? (
                    <span
                      className={
                        tracking.carrierStatus === "delivered"
                          ? "text-green-800"
                          : tracking.carrierStatus === "exception" ||
                              tracking.carrierStatus === "returned"
                            ? "text-red-800"
                            : "text-gray-900"
                      }
                    >
                      {tracking.carrierStatusLabel}
                    </span>
                  ) : (
                    <span className="font-normal text-gray-600">henüz sorgulanmadı</span>
                  )}
                </p>
                {tracking.carrierDescription ? (
                  <p className="mt-0.5 text-xs text-gray-700">{tracking.carrierDescription}</p>
                ) : null}
                {tracking.deliveredAt ? (
                  <p className="mt-0.5 text-xs text-green-800">
                    Teslim: {date(tracking.deliveredAt)}
                  </p>
                ) : null}
                {tracking.checkedAt ? (
                  <p className="mt-0.5 text-xs text-gray-600">
                    Son sorgu: {dt(tracking.checkedAt)}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="noprint btn-secondary shrink-0"
                onClick={() => void onTrack()}
                disabled={trackAction.pending}
              >
                {trackAction.pending ? "Sorgulanıyor…" : "Takip et"}
              </button>
            </div>

            {/* Hareket geçmişi — en yeniden eskiye, taşıyıcının kendi metniyle. */}
            {tracking.events && tracking.events.length > 0 ? (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-blue-700">
                  Hareket geçmişi ({tracking.events.length})
                </summary>
                <ul className="mt-2 space-y-1">
                  {tracking.events.map((e, i) => (
                    <li key={i} className="text-xs text-gray-700">
                      <span className="tabular-nums text-gray-600">
                        {e.at ? dt(new Date(e.at)) : "—"}
                      </span>{" "}
                      · {e.description || "—"}
                      {e.location ? ` · ${e.location}` : ""}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        ) : null}

        {/* ★ SHOPIFY'A YAZILAMAMIŞ KAYIT GÖRÜNÜR OLMALI.
            Sunucu `trackingInfo.shopifySync` damgasını yazıyor ama hiçbir ekran
            okumuyordu: takip numarası girilmiş ama müşteriye bildirim gitmemiş
            sipariş, ekranda normal kargolanmış siparişten ayırt edilemiyordu.
            Sebep (izin/kapsam mesajı) burada aynen yazılır ve düzeldiğinde tek
            düğmeyle yeniden gönderilir. */}
        {tracking.shopifySync && tracking.shopifySync.ok === false ? (
          tracking.shopifySync.state === "skipped" ? (
            <p className="mt-3 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              Bu sipariş Shopify siparişi değil (Trendyol ya da eski sistemden göç etmiş kayıt);
              takip bilgisi yalnızca Balder'de tutulur.
            </p>
          ) : (
            <div role="alert" className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
              <p className="font-semibold">
                Takip numarası Shopify'a yazılamadı — müşteriye kargo bildirimi GİTMEDİ.
              </p>
              {tracking.shopifySync.error ? (
                <p className="mt-1 break-words text-xs text-red-800">
                  Sebep: {tracking.shopifySync.error}
                </p>
              ) : null}
              {isStaff ? (
                <button
                  type="button"
                  className="noprint btn-secondary mt-2"
                  onClick={() => void onRetryFulfillment()}
                  disabled={retryFulfillmentAction.pending}
                >
                  {retryFulfillmentAction.pending ? "Gönderiliyor…" : "Shopify'a yeniden gönder"}
                </button>
              ) : null}
            </div>
          )
        ) : null}

        {isStaff ? (
          <form className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSaveTracking}>
            <div>
              <label htmlFor="provider" className="label">
                Kargo firması
              </label>
              <select
                id="provider"
                name="provider"
                className="select mt-1"
                value={formProvider}
                onChange={(e) => setFormProvider(e.target.value)}
              >
                {CARGO_PROVIDERS.map((p) => (
                  <option key={p.label} value={p.label}>
                    {p.label}
                  </option>
                ))}
                <option value="">Özel Sağlayıcı</option>
              </select>
            </div>

            {/* Özel ad YALNIZCA "Özel Sağlayıcı" seçildiğinde anlamlı; UPS
                seçiliyken görünmesi kutuyu ölü gösteriyordu. */}
            {formProvider === "" ? (
              <div>
                <label htmlFor="customProvider" className="label">
                  Özel sağlayıcı adı <span className="text-red-600">*</span>
                </label>
                <input
                  id="customProvider"
                  name="customProvider"
                  type="text"
                  required
                  className="input mt-1"
                  value={formCustomProvider}
                  onChange={(e) => setFormCustomProvider(e.target.value)}
                  maxLength={120}
                  placeholder="Örn. Sürat Kargo"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Müşteriye giden kargo e-postasında firma adı olarak bu yazacak.
                </p>
              </div>
            ) : (
              <div className="hidden md:block" aria-hidden="true" />
            )}

            <div>
              <label htmlFor="trackingNumber" className="label">
                Takip numarası <span className="text-red-600">*</span>
              </label>
              <input
                id="trackingNumber"
                name="trackingNumber"
                type="text"
                required
                className="input mt-1"
                value={formNumber}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormNumber(v);
                  // M… numara Horoz'dur; firma UPS/özel bırakıldıysa kendiliğinden geçer.
                  if (looksLikeHorozNumber(v) && formProvider !== "Horoz Lojistik") setFormProvider("Horoz Lojistik");
                }}
                maxLength={64}
              />
            </div>

            <div>
              <label htmlFor="trackingUrl" className="label">
                Takip bağlantısı
              </label>
              <input
                id="trackingUrl"
                name="trackingUrl"
                type="url"
                className="input mt-1"
                value={formUrl}
                onChange={(e) => {
                  setUrlTouched(true);
                  setFormUrl(e.target.value);
                }}
                maxLength={500}
                placeholder="https://…"
              />
              {!urlTouched && formUrl ? (
                <p className="mt-1 text-xs text-gray-500">
                  Firma kalıbından otomatik üretildi; elle değiştirebilirsiniz.
                </p>
              ) : null}
              {urlTouched && formUrl !== (buildTrackingUrl(formProvider, formNumber) ?? "") ? (
                <button
                  type="button"
                  className="mt-1 text-xs text-gray-600 underline-offset-2 hover:underline"
                  onClick={() => {
                    setUrlTouched(false);
                    setFormUrl(buildTrackingUrl(formProvider, formNumber) ?? "");
                  }}
                >
                  Otomatik bağlantıya dön
                </button>
              ) : null}
              {/* ★ BAĞLANTI TIKLANABİLİR OLMALI.
                  Adres salt okunur bir kutuda duruyordu; kargo takibine bakmak
                  için metni seçip kopyalayıp yeni sekmeye yapıştırmak
                  gerekiyordu. Kargo sorgusu günde onlarca kez yapılan bir iş —
                  üç adımlık bir hareket olmamalı. Alan hâlâ düzenlenebilir;
                  altına açma ve kopyalama kısayolları eklendi. */}
              {formUrl ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-3">
                  <a
                    href={formUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link inline-flex items-center gap-1 text-sm"
                  >
                    Takibi aç
                    <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path d="M11 3a1 1 0 1 0 0 2h2.586l-6.293 6.293a1 1 0 1 0 1.414 1.414L15 6.414V9a1 1 0 1 0 2 0V4a1 1 0 0 0-1-1h-5Z" />
                      <path d="M5 5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-3a1 1 0 1 0-2 0v3H5V7h3a1 1 0 0 0 0-2H5Z" />
                    </svg>
                  </a>
                  <button
                    type="button"
                    className="text-sm text-gray-600 underline-offset-2 hover:underline"
                    onClick={() => {
                      navigator.clipboard
                        .writeText(formUrl)
                        .then(() => toast.success("Takip bağlantısı kopyalandı."))
                        .catch(() => toast.error("Kopyalanamadı."));
                    }}
                  >
                    Kopyala
                  </button>
                </div>
              ) : null}
            </div>

            <div className="flex items-center gap-2 md:col-span-2">
              <input
                id="notifyCustomer"
                name="notifyCustomer"
                type="checkbox"
                defaultChecked
                className="h-4 w-4 rounded border-gray-300 text-primary-600"
              />
              <label htmlFor="notifyCustomer" className="text-sm text-gray-700">
                Müşteriye kargo bildirimi gönderilsin
              </label>
            </div>

            <div className="noprint flex items-center gap-2 md:col-span-2">
              <button type="submit" className="btn-primary" disabled={trackingAction.pending}>
                {trackingAction.pending ? "Kaydediliyor…" : "Kargo bilgisini kaydet"}
              </button>
              {tracking.shippedAt ? (
                <span className="text-xs text-gray-500">
                  Gönderim tarihi: {date(tracking.shippedAt)}
                </span>
              ) : null}
            </div>

            {trackingAction.error ? (
              <p role="alert" className="md:col-span-2 text-sm text-red-800">
                {trackingAction.error}
              </p>
            ) : null}
          </form>
        ) : (
          <dl className="mt-3 space-y-1 text-sm">
            <div className="flex gap-2">
              <dt className="text-gray-500">Firma:</dt>
              {/* Salt okunur görünüm de aynı çözümlemeyi kullanır: kayıtlı ad
                  yer tutucu ise numaradan tanınan firma yazılır. */}
              <dd className="text-gray-900">
                {resolveProvider(currentProvider, currentNumber) ||
                  (isPlaceholderProvider(tracking.customProvider ?? "")
                    ? "—"
                    : tracking.customProvider)}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-gray-500">Takip no:</dt>
              <dd className="text-gray-900">{currentNumber || "—"}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-gray-500">Gönderim:</dt>
              <dd className="text-gray-900">{date(tracking.shippedAt)}</dd>
            </div>
          </dl>
        )}
      </section>

      {/* Adresler — ekranda açılır bölüm; yazdırmada (sipariş fişi) her zaman tam basılır. */}
      <div className="noprint">
        <Collapsible
          storageKey="siparis.adresler"
          title="Fatura ve kargo adresi"
          meta={[
            order.shippingAddress?.city ?? order.billingAddress?.city,
            order.customer?.phone,
            order.customer?.email,
          ]
            .filter(Boolean)
            .join(" · ")}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <AddressBlock
              title="Fatura adresi"
              a={order.billingAddress}
              email={order.customer?.email}
              phone={order.customer?.phone}
            />
            <AddressBlock title="Kargo adresi" a={order.shippingAddress} />
          </div>
        </Collapsible>
      </div>
      <div className="print-only">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <AddressBlock
          title="Fatura adresi"
          a={order.billingAddress}
          email={order.customer?.email}
          phone={order.customer?.phone}
        />
        <AddressBlock title="Kargo adresi" a={order.shippingAddress} />
      </div>
      </div>

      {/* SSH kayıtları */}
      <section aria-labelledby="ssh-baslik">
        <h2 id="ssh-baslik" className="text-sm font-semibold text-gray-900">
          Satış sonrası hizmet (SSH) kayıtları
        </h2>
        {sshQ.loading ? (
          <LoadingBlock />
        ) : sshQ.error ? (
          <p role="alert" className="mt-2 text-sm text-red-800">
            {sshQ.error}
          </p>
        ) : sshQ.docs.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">Bu siparişe açılmış SSH kaydı yok.</p>
        ) : (
          <ul role="list" className="mt-2 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
            {sshQ.docs.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  {/*
                    Eski sistemde bu link edit_ssh.php'ye gidiyordu — dosya projede YOKTU,
                    404 veriyordu.

                    ★ BURADA DA AYNI HATA TEKRARLANMIŞTI: `/after-sales/{id}` diye bir
                    ROTA YOK (App.tsx yalnızca `after-sales` listesini tanımlar), yani
                    her SSH bağlantısı "Sayfa bulunamadı" ekranına düşüyordu. Uygulamanın
                    geri kalanı bunu zaten biliyor: `Notifications.tsx` SSH referansını
                    `() => "/after-sales"` ile kimliksiz eşliyor.

                    Kayda gerçekten götüren tek yol, listeyi kaydın KENDİ statüsüne
                    süzmektir (`AfterSales.tsx` `?status=` parametresini okur). Statü
                    yoksa süzgeçsiz listeye gidilir — boş ekran yerine dolu liste.
                  */}
                  <Link
                    to={s.status ? `/after-sales?status=${encodeURIComponent(s.status)}` : "/after-sales"}
                    className="link text-sm font-medium"
                  >
                    {s.productTitle ?? `SSH-${s.id}`}
                  </Link>
                  <p className="mt-0.5 truncate text-xs text-gray-500">{s.problemNote ?? "—"}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3 text-sm">
                  <span className="text-gray-600">{n(s.volume, " adet")}</span>
                  <StatusBadge label={s.statusLabel} technicName={s.status} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default OrderDetail;
